import React, { useState, useEffect } from 'react';
import radiologyResultService from '../../services/radiologyResultService';
import './RadiologyBulkDocxModal.css';

const RadiologyBulkDocxModal = ({ isOpen, onClose, currentDoctorId = null, currentCentreId = null }) => {
  const [activeTab, setActiveTab] = useState('import'); // 'import' | 'export'
  const [file, setFile] = useState(null);
  const [investigationId, setInvestigationId] = useState(3); // Default to Chest X-Ray
  const [doctorId, setDoctorId] = useState(currentDoctorId || '');
  const [centreId, setCentreId] = useState(currentCentreId || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Sample investigations for mapping
  const investigations = [
    { id: 3, name: 'X-Ray Chest PA View (Modality: X-Ray)' },
    { id: 4, name: 'Ultrasound Whole Abdomen (Modality: USG)' },
    { id: 5, name: 'MRI Brain with Contrast (Modality: MRI)' },
    { id: 6, name: 'CT Head / Brain Non-Contrast (Modality: CT)' }
  ];

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setImportResult(null);
      setErrorMessage('');
      setIsProcessing(false);
    } else {
      if (currentDoctorId) setDoctorId(currentDoctorId);
      if (currentCentreId) setCentreId(currentCentreId);
    }
  }, [isOpen, currentDoctorId, currentCentreId]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (!selected.name.endsWith('.zip')) {
        setErrorMessage('Only ZIP archive packages (.zip) containing DOCX templates are accepted.');
        setFile(null);
        return;
      }
      setFile(selected);
      setErrorMessage('');
      setImportResult(null);
    }
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setErrorMessage('Please select a valid ZIP archive containing .docx files.');
      return;
    }
    if (!investigationId) {
      setErrorMessage('Please select a target investigation to map these templates.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');
    setImportResult(null);

    try {
      const resp = await radiologyResultService.bulkImportZip(
        file,
        investigationId,
        doctorId ? parseInt(doctorId, 10) : null,
        doctorId ? 'Assigned Doctor' : null,
        centreId ? parseInt(centreId, 10) : null
      );

      if (resp && resp.data) {
        setImportResult(resp.data);
      } else {
        setErrorMessage('Import response was empty.');
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || err.message || 'Error occurred during bulk template import.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setErrorMessage('');

    try {
      const blob = await radiologyResultService.bulkExportZip(
        investigationId ? parseInt(investigationId, 10) : null,
        doctorId ? parseInt(doctorId, 10) : null,
        centreId ? parseInt(centreId, 10) : null
      );

      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Radiology_Templates_Export_${new Date().toISOString().slice(0, 10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || err.message || 'Error occurred exporting templates.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="rad-modal-backdrop">
      <div className="rad-bulk-modal">
        <div className="rad-bulk-header">
          <div className="rad-bulk-title-group">
            <span className="rad-bulk-icon">📦</span>
            <div>
              <h3>Bulk DOCX Template Management</h3>
              <p className="rad-bulk-subtitle">High-volume template migration, backup, and department onboarding</p>
            </div>
          </div>
          <button className="rad-bulk-close" onClick={onClose}>✕</button>
        </div>

        <div className="rad-bulk-tabs">
          <button
            className={`rad-tab-btn ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => setActiveTab('import')}
          >
            📥 Bulk Import (ZIP → DOCX)
          </button>
          <button
            className={`rad-tab-btn ${activeTab === 'export' ? 'active' : ''}`}
            onClick={() => setActiveTab('export')}
          >
            📤 Bulk Export (DOCX Archive)
          </button>
        </div>

        {errorMessage && (
          <div className="rad-bulk-alert error">
            <span>⚠️</span> {errorMessage}
          </div>
        )}

        <div className="rad-bulk-body">
          {activeTab === 'import' ? (
            <form onSubmit={handleImportSubmit} className="rad-import-form">
              <div className="rad-form-section">
                <label className="rad-label">Select ZIP File (.zip containing .docx templates)</label>
                <div className="rad-file-dropzone">
                  <input
                    type="file"
                    accept=".zip"
                    id="rad-zip-file-input"
                    onChange={handleFileChange}
                    className="rad-file-input"
                  />
                  <label htmlFor="rad-zip-file-input" className="rad-file-label">
                    {file ? (
                      <span className="file-name">📄 {file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                    ) : (
                      <span>📁 Drag & Drop ZIP archive here or <strong>Browse Files</strong></span>
                    )}
                  </label>
                </div>
              </div>

              <div className="rad-form-grid">
                <div className="rad-form-field">
                  <label className="rad-label">Target Investigation *</label>
                  <select
                    value={investigationId}
                    onChange={(e) => setInvestigationId(e.target.value)}
                    className="rad-select"
                    required
                  >
                    {investigations.map(inv => (
                      <option key={inv.id} value={inv.id}>{inv.name}</option>
                    ))}
                  </select>
                </div>

                <div className="rad-form-field">
                  <label className="rad-label">Scope / Doctor Ownership (Optional)</label>
                  <select
                    value={doctorId}
                    onChange={(e) => setDoctorId(e.target.value)}
                    className="rad-select"
                  >
                    <option value="">Shared (All Radiologists)</option>
                    <option value="1">Personal: Dr. Admin (Current User)</option>
                    <option value="2">Personal: Dr. Senior Consultant</option>
                  </select>
                </div>
              </div>

              {importResult && (
                <div className="rad-import-summary">
                  <div className="rad-summary-badges">
                    <span className="badge processed">Total: {importResult.totalProcessed}</span>
                    <span className="badge success">✅ Succeeded: {importResult.successCount}</span>
                    <span className="badge failure">❌ Failed: {importResult.failureCount}</span>
                  </div>

                  <div className="rad-results-table-wrap">
                    <table className="rad-results-table">
                      <thead>
                        <tr>
                          <th>File Name</th>
                          <th>Template Title</th>
                          <th>Status</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.results.map((item, idx) => (
                          <tr key={idx} className={item.success ? 'row-success' : 'row-failure'}>
                            <td>{item.fileName}</td>
                            <td><strong>{item.templateTitle}</strong></td>
                            <td>
                              <span className={`status-pill ${item.success ? 'success' : 'failure'}`}>
                                {item.success ? 'Imported' : 'Error'}
                              </span>
                            </td>
                            <td>{item.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="rad-bulk-actions">
                <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!file || isProcessing}
                >
                  {isProcessing ? 'Validating & Importing...' : 'Validate & Import Templates'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleExportSubmit} className="rad-export-form">
              <div className="rad-export-info">
                <p>
                  Export all active templates formatted as individual Microsoft Word (.docx) files, packaged into a single ZIP archive.
                </p>
              </div>

              <div className="rad-form-grid">
                <div className="rad-form-field">
                  <label className="rad-label">Filter by Investigation</label>
                  <select
                    value={investigationId}
                    onChange={(e) => setInvestigationId(e.target.value)}
                    className="rad-select"
                  >
                    <option value="">All Radiology Investigations</option>
                    {investigations.map(inv => (
                      <option key={inv.id} value={inv.id}>{inv.name}</option>
                    ))}
                  </select>
                </div>

                <div className="rad-form-field">
                  <label className="rad-label">Filter by Scope / Doctor</label>
                  <select
                    value={doctorId}
                    onChange={(e) => setDoctorId(e.target.value)}
                    className="rad-select"
                  >
                    <option value="">All Templates (Shared + Personal)</option>
                    <option value="1">My Templates Only</option>
                  </select>
                </div>
              </div>

              <div className="rad-bulk-actions">
                <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Generating ZIP Archive...' : 'Download Templates ZIP'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RadiologyBulkDocxModal;
